import React from 'react'
import type { ComponentConfig, Fields } from '@puckeditor/core'
import { WithLayout, withLayout } from '../../puck/components/Layout'
import { ThemeCustomFields } from '../../puck/theme-config'
import PromoStep from './PromoStep'

/*
  Puck block that drops the promo offers grid into a puck step (BR-5419).

  This is purely the presentation seam: Puck owns the styling/layout around the
  grid (via withLayout), while the offers themselves stay owned by beaker2 —
  PromoStep reads the step id off puck metadata and fetches from
  /promo-step, exactly as the standalone PROMO step does. Nothing on
  the beaker2 / API side is aware this block exists.
*/
export type OffersSectionFields = {
  /*
    Which beaker2 step's offers to show. Left blank, the block uses the step it
    lives in (via puck metadata) — the standalone case. Set explicitly, an
    author can point the block at any promo step's offers from anywhere in Puck.
  */
  stepId?: string
  columns?: 'auto' | '1' | '2' | '3'
  showFilter?: boolean
  showSort?: boolean
  emptyMessage?: string
}

export type OffersSectionProps = WithLayout<OffersSectionFields>

export const OFFERS_SECTION_FIELDS: Fields<
  OffersSectionProps,
  ThemeCustomFields[keyof ThemeCustomFields]
> = {
    stepId: {
      type: 'promoStepPicker',
      label: 'Promo Step',
    },
    columns: {
      type: 'radio',
      label: 'Layout',
      options: [
        { label: 'Auto', value: 'auto' },
        { label: '1 Column', value: '1' },
        { label: '2 Columns', value: '2' },
        { label: '3 Columns', value: '3' },
      ],
    },
    showFilter: { type: 'boolean', label: 'Show Filter' },
    showSort: { type: 'boolean', label: 'Show Sort' },
    emptyMessage: { type: 'text', label: 'Empty State Message' },
}

const offersSectionInternal: ComponentConfig<OffersSectionProps> = {
  label: 'Offers Section',
  fields: OFFERS_SECTION_FIELDS,
  defaultProps: {
    stepId: '',
    columns: 'auto',
    showFilter: true,
    showSort: true,
    // Left blank so PromoStep renders its own translated default; an author
    // typing here overrides it.
    emptyMessage: '',
    layout: {
      maxWidth: '992px',
      centered: true,
      padding: { top: '20px', right: '20px', bottom: '20px', left: '20px' },
    },
  },
  render: ({
    stepId,
    columns,
    showFilter,
    showSort,
    emptyMessage,
    puck,
  }) => {
    // PuckStep puts the enriched step on metadata at render time; the editor
    // passes { step: { stepId, originalStepId } } while authoring. originalStepId
    // is the beaker2 id offers are keyed on (step.id is the CMS step).
    const metadataStep = (
      puck?.metadata as {
        step?: {
          stepId?: number | string
          originalStepId?: number | string
          promo?: { headline?: string; subcopy?: string }
        }
      }
    )?.step
    // An author-picked Promo Step ID wins; otherwise fall back to the step the
    // block lives in.
    const resolvedStepId =
      (stepId && stepId.trim()) ||
      metadataStep?.originalStepId ||
      metadataStep?.stepId
    // MUI (DropdownSelect) uses Emotion, which crashes in Puck's iframe canvas
    // because the Emotion style cache targets the outer document's <head>.
    // Suppress the filter/sort controls in the editor to avoid the error;
    // they serve no purpose there anyway.
    const isEditing = puck?.isEditing
    return (
      <PromoStep
        step={resolvedStepId ? {
          stepId: resolvedStepId,
          promo: metadataStep?.promo,
        } : undefined}
        columns={columns}
        showFilter={isEditing ? false : showFilter}
        showSort={isEditing ? false : showSort}
        emptyMessage={emptyMessage}
      />
    )
  },
}

export const offersSectionConfig = withLayout(offersSectionInternal)
